import { date } from '@ipfabric/utils';
import { Particle, type ParticleProps } from './Particle';
import { Vector } from './Vector';

type EmitterProps = ParticleProps & {
  count: number;
  rate: number;
  windAngle: number;
  windSpeed: number;
};

export class Emitter {
  particles: Particle[] = [];

  private canvas: HTMLCanvasElement;
  private lastTime = 0;
  private emitting = true;
  private pos: Vector;
  private props: EmitterProps;

  constructor(
    canvas: HTMLCanvasElement,
    x = 0,
    y = 0,
    props: Partial<EmitterProps> = {},
  ) {
    this.canvas = canvas;
    this.pos = new Vector(x, y);

    this.props = {
      size: 200,
      count: 4,
      rate: 50,
      speed: 4,
      fade: 3,
      invert: true,
      angle: -90,
      spread: 15,
      bounceX: 1,
      bounceY: 1,
      windAngle: 0,
      windSpeed: 0.03,
      ...props,
    };
  }

  public prop<K extends keyof EmitterProps>(key: K, val: EmitterProps[K]): this;
  public prop(props: Partial<EmitterProps>): this;
  public prop<K extends keyof EmitterProps>(
    keyOrObj: K | Partial<EmitterProps>,
    val?: EmitterProps[K],
  ): this {
    if (typeof keyOrObj === 'object') {
      Object.assign(this.props, keyOrObj);
    } else {
      this.props[keyOrObj] = val as EmitterProps[K];
    }
    return this;
  }

  public add = (): this => {
    this.particles.push(
      new Particle(this.canvas, this.pos.x, this.pos.y, this.props),
    );
    return this;
  };

  public loop = (): this => {
    const force = Vector.fromAngle(this.props.windAngle, this.props.windSpeed);
    this.particles = this.particles.filter((p) => {
      p.setForce(force);
      p.loop();
      return p.life > 0;
    });

    const now = date.getTs();
    if (this.emitting && now - this.lastTime > this.props.rate) {
      for (let i = 0; i < this.props.count; i++) {
        this.add();
      }
      this.lastTime = now;
    }

    return this;
  };

  public start = (): this => {
    this.emitting = true;
    return this;
  };

  public stop = (): this => {
    this.emitting = false;
    return this;
  };

  get x(): number {
    return this.pos.x;
  }

  set x(value: number) {
    this.pos.x = value;
  }

  get y(): number {
    return this.pos.y;
  }

  set y(value: number) {
    this.pos.y = value;
  }
}
